import { useEffect, useState } from "react";

import {
	api,
	type AgentInfo,
	type FileFromGitHub,
	type FileKind,
} from "../lib/api.ts";
import { AgentStatusBadge } from "./AgentStatusBadge.tsx";
import { RunCommand } from "./RunCommand.tsx";

/**
 * Self-contained card for a single project-agent — status, run command,
 * prompt + AGENT.md editors. Reused by the per-agent page (/agents/:key).
 *
 * `businessId` is needed because the file endpoints are nested under
 * /api/businesses/:id/agents/:key/file — they predate the standalone agent
 * routes. When we add /api/agents/:key/file later this prop goes away.
 */
export function AgentDetailCard({
	businessId,
	agent,
	editEnabled,
	defaultOpenKind = null,
	deploySha,
}: {
	businessId: string;
	agent: AgentInfo;
	editEnabled: boolean;
	defaultOpenKind?: FileKind | null;
	/** Worker bundle git SHA — rendered as a 5th badge alongside the per-agent
	 *  content hashes. Optional: older Workers don't return it. */
	deploySha?: string;
}) {
	const [openKind, setOpenKind] = useState<FileKind | null>(defaultOpenKind);

	return (
		<div className="border border-zinc-800 rounded-lg bg-zinc-900/40">
			<div className="p-4 flex items-start justify-between gap-3">
				<div className="min-w-0 flex-1">
					<div className="text-base font-semibold text-zinc-100">{agent.displayName}</div>
					<div className="text-xs text-zinc-500 mt-0.5 font-mono">{agent.key}</div>
					{agent.parent && (
						<div className="text-xs text-zinc-500 mt-1">sub-agent of {agent.parent}</div>
					)}
					<div className="mt-2">
						<AgentStatusBadge label={agent.key} />
					</div>
				</div>
				<div className="flex gap-2">
					<button
						type="button"
						onClick={() => setOpenKind(openKind === "prompt" ? null : "prompt")}
						className="text-xs px-3 py-1 rounded border border-zinc-700 hover:border-zinc-600 text-zinc-300"
					>
						{openKind === "prompt" ? "Close" : "System prompt"}
					</button>
					<button
						type="button"
						onClick={() => setOpenKind(openKind === "agent_md" ? null : "agent_md")}
						className="text-xs px-3 py-1 rounded border border-zinc-700 hover:border-zinc-600 text-zinc-300"
					>
						{openKind === "agent_md" ? "Close" : "AGENT.md"}
					</button>
					<button
						type="button"
						onClick={() => setOpenKind(openKind === "knowledge" ? null : "knowledge")}
						className="text-xs px-3 py-1 rounded border border-zinc-700 hover:border-zinc-600 text-zinc-300"
					>
						{openKind === "knowledge" ? "Close" : "KNOWLEDGE.md"}
					</button>
				</div>
			</div>

			<div className="px-4 pb-3 space-y-2">
				<RunCommand label={agent.key} />
				{agent.versions && (
					<div className="flex flex-wrap gap-1.5 text-[11px]">
						<span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 font-mono" title="content hash of prompt.md">
							prompt @{agent.versions.prompt}
						</span>
						<span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 font-mono" title="content hash of AGENT.md">
							doc @{agent.versions.agentMd}
						</span>
						<span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 font-mono" title="content hash of KNOWLEDGE.md">
							kb @{agent.versions.knowledge}
						</span>
						<span className="px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 font-mono" title="agent version = hash(prompt + AGENT.md + agent.json)">
							agent @{agent.versions.agent}
						</span>
						{deploySha && deploySha !== "dev" && (
							<span className="px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-200 font-mono" title="git SHA the Worker bundle was built from">
								deploy @{deploySha}
							</span>
						)}
					</div>
				)}
				<div className="text-xs text-zinc-500 font-mono">
					<div>tasks project_id: {agent.tasksProjectId}</div>
					<div>prompt: {agent.files.systemPrompt}</div>
					<div>doc: {agent.files.agentMd}</div>
					<div>kb: {agent.files.knowledge}</div>
				</div>
			</div>

			{openKind && (
				<FileEditor
					businessId={businessId}
					agentKey={agent.key}
					kind={openKind}
					editEnabled={editEnabled}
				/>
			)}
		</div>
	);
}

function FileEditor({
	businessId,
	agentKey,
	kind,
	editEnabled,
}: {
	businessId: string;
	agentKey: string;
	kind: FileKind;
	editEnabled: boolean;
}) {
	const [file, setFile] = useState<FileFromGitHub | null>(null);
	const [draft, setDraft] = useState<string>("");
	const [commitMessage, setCommitMessage] = useState<string>("");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [prUrl, setPrUrl] = useState<string | null>(null);

	useEffect(() => {
		setFile(null);
		setDraft("");
		setError(null);
		setPrUrl(null);
		api
			.getAgentFile(businessId, agentKey, kind)
			.then((f) => {
				setFile(f);
				setDraft(f.content);
				setCommitMessage(
					`chore(${agentKey}): edit ${kind === "prompt" ? "system prompt" : "AGENT.md"} via UI`,
				);
			})
			.catch((err) => setError((err as Error).message));
	}, [businessId, agentKey, kind]);

	const dirty = file !== null && draft !== file.content;

	async function handlePropose() {
		if (!file || !dirty || busy) return;
		setBusy(true);
		setError(null);
		try {
			const result = await api.proposeAgentFileEdit(businessId, agentKey, {
				kind,
				sha: file.sha,
				content: draft,
				commitMessage,
			});
			setPrUrl(result.htmlUrl);
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	}

	if (error) {
		return (
			<div className="border-t border-zinc-800 px-4 py-3 text-sm text-red-300">{error}</div>
		);
	}
	if (!file) {
		return (
			<div className="border-t border-zinc-800 px-4 py-3 text-sm text-zinc-500">Loading…</div>
		);
	}

	return (
		<div className="border-t border-zinc-800">
			<div className="px-4 pt-3 flex items-baseline justify-between">
				<div className="text-xs text-zinc-500 font-mono">
					{file.path}{" "}
					<a
						href={file.htmlUrl}
						target="_blank"
						rel="noreferrer"
						className="text-zinc-400 hover:text-zinc-200 underline"
					>
						↗ GitHub
					</a>
				</div>
				<div className="text-xs text-zinc-500">
					{draft.length} chars{dirty && " · modified"}
				</div>
			</div>
			{kind === "prompt" && (
				<div className="px-4 pt-1 text-xs text-zinc-500">
					Plain markdown — <code>agents/&lt;key&gt;/prompt.md</code>. Edit + Save opens a single-file
					PR; merge + deploy regenerates the bundle.
				</div>
			)}
			<textarea
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				className="mt-2 w-full h-96 px-4 py-2 bg-terminal/70 text-zinc-200 text-sm font-mono resize-y outline-none border-t border-b border-zinc-800"
				spellCheck={false}
				disabled={!editEnabled}
			/>
			<div className="px-4 py-3 flex items-center gap-3">
				<input
					type="text"
					value={commitMessage}
					onChange={(e) => setCommitMessage(e.target.value)}
					placeholder="Commit message"
					className="flex-1 px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-200 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
					disabled={!editEnabled || busy}
				/>
				<button
					type="button"
					onClick={handlePropose}
					disabled={!editEnabled || !dirty || busy || commitMessage.trim().length === 0}
					className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium rounded"
				>
					{busy ? "Opening PR…" : "Propose change (PR)"}
				</button>
			</div>
			{prUrl && (
				<div className="px-4 pb-4 text-sm text-emerald-300">
					✓ PR opened:{" "}
					<a href={prUrl} target="_blank" rel="noreferrer" className="underline">
						{prUrl}
					</a>
					<div className="text-xs text-zinc-500 mt-1">
						Review and merge in GitHub. Auto-deploy on merge picks up Worker changes; local
						project-agents need a <code>git pull</code> + restart.
					</div>
				</div>
			)}
			{!editEnabled && (
				<div className="px-4 pb-3 text-xs text-zinc-500 italic">
					GitHub PR-flow not configured on this Worker (missing GITHUB_TOKEN secret). Read-only.
				</div>
			)}
		</div>
	);
}
